import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  Stack,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { baseAxios } from "../../utils/config";
import { toast } from "react-hot-toast";
import jsPDF from "jspdf";
import {
  PAYMENT_METHODS,
  getPaymentMethodLabel,
} from "../../utils/paymentMethods";
import {
  dialogPaperSx,
  dialogTitleSx,
  dialogCloseIconSx,
  primaryBtnSx,
  secondaryBtnSx,
  outlinedBrandBtnSx,
} from "./schedulerDialogStyles";
import { colors } from "../../theme/colors";

const TransactionSummaryModal = ({
  open,
  onClose,
  onBack,
  transactionData,
  onSuccess,
}) => {
  const [paymentMethod, setPaymentMethod] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [tipAmount, setTipAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const trimmedEmailAddress = emailAddress.trim();
  const isValidEmailAddress = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmailAddress),
    [trimmedEmailAddress]
  );

  // Reset form state
  const resetFormState = () => {
    setPaymentMethod("");
    setEmailAddress("");
    setTipAmount("");
  };

  // Reset email when transaction data changes
  useEffect(() => {
    if (transactionData?.clientEmail) {
      setEmailAddress(transactionData.clientEmail);
    }
  }, [transactionData?.clientEmail]);

  // Calculate total with tip
  const totalWithTip = useMemo(() => {
    const tip = parseFloat(tipAmount.replace(/[^0-9.]/g, "")) || 0;
    return (transactionData?.totalAmount || 0) + tip;
  }, [transactionData?.totalAmount, tipAmount]);

  const handleTipChange = (event) => {
    const value = event.target.value.replace(/[^0-9.]/g, "");
    const decimalCount = (value.match(/\./g) || []).length;
    if (decimalCount > 1) return;
    const parts = value.split(".");
    if (parts[1] && parts[1].length > 2) return;
    setTipAmount(value);
  };

  const generatePdfBlob = async () => {
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      pdf.setFont("helvetica", "normal");

      // Header
      pdf.setFontSize(16);
      pdf.text("*** RECEIPT ***", 105, 20, { align: "center" });

      pdf.setFontSize(12);
      pdf.text("Walk-In Transaction", 105, 30, { align: "center" });

      // Date and time
      pdf.setFontSize(10);
      const currentDate = new Date().toLocaleDateString();
      const currentTime = new Date().toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
      pdf.text(`${currentDate} ${currentTime}`, 20, 45);

      // Client name
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text(transactionData?.clientName || "Walk-In Client", 20, 60);

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text("Served by: Front Desk", 20, 68);

      // Table headers
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.text("ITEM", 20, 85);
      pdf.text("TYPE", 100, 85);
      pdf.text("PRICE", 150, 85);

      // Draw line under headers
      pdf.line(20, 87, 190, 87);

      let currentY = 95;

      // Items
      pdf.setFont("helvetica", "normal");
      transactionData?.items?.forEach((item) => {
        if (currentY > 270) {
          pdf.addPage();
          currentY = 20;
        }

        pdf.text(item.name, 20, currentY);
        pdf.text(item.type.charAt(0).toUpperCase() + item.type.slice(1), 100, currentY);
        pdf.text(`$${parseFloat(item.price).toFixed(2)}`, 150, currentY);
        currentY += 8;
      });

      // Tip amount
      const tipValue = parseFloat(tipAmount.replace(/[^0-9.]/g, "")) || 0;
      if (tipValue > 0) {
        if (currentY > 270) {
          pdf.addPage();
          currentY = 20;
        }
        pdf.text("Tip", 20, currentY);
        pdf.text(`$${tipValue.toFixed(2)}`, 150, currentY);
        currentY += 8;
      }

      // Summary section
      currentY += 10;
      if (currentY > 270) {
        pdf.addPage();
        currentY = 20;
      }

      pdf.line(20, currentY, 190, currentY);
      currentY += 8;

      pdf.text("Subtotal", 20, currentY);
      pdf.text(`$${transactionData?.totalAmount?.toFixed(2) || "0.00"}`, 150, currentY);
      currentY += 8;

      if (tipValue > 0) {
        pdf.text("Tip", 20, currentY);
        pdf.text(`$${tipValue.toFixed(2)}`, 150, currentY);
        currentY += 8;
      }

      pdf.setFont("helvetica", "bold");
      pdf.text("TOTAL", 20, currentY);
      pdf.text(`$${totalWithTip.toFixed(2)}`, 150, currentY);
      currentY += 8;

      pdf.setFont("helvetica", "normal");
      pdf.text(
        getPaymentMethodLabel(paymentMethod),
        20,
        currentY
      );
      pdf.text(`$${totalWithTip.toFixed(2)}`, 150, currentY);

      // Footer
      currentY += 20;
      if (currentY > 270) {
        pdf.addPage();
        currentY = 20;
      }

      pdf.setFont("helvetica", "bold");
      pdf.text("Thank you for your visit!", 105, currentY, { align: "center" });

      const pdfBlob = pdf.output("blob");
      return pdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      return null;
    }
  };

  const completeTransaction = async ({ sendEmail = false, download = false } = {}) => {
    if (paymentMethod === "") {
      toast.error("Please select a payment method.");
      return;
    }

    if (sendEmail && !isValidEmailAddress) {
      toast.error("Please enter a valid email address to send receipt.");
      return;
    }

    setIsProcessing(true);

    try {
      // Separate products from services
      const productItems = transactionData.items.filter(item => item.type === "product");

      // Format all items for the checkout API
      const formattedServices = transactionData.items.map(item => ({
        service_id: item.referenceId,
        service_name: item.name,
        servicePrice: item.price,
        type: item.type,
      }));

      // Format products for inventory update
      const formattedProducts = productItems.map(item => ({
        productId: item.referenceId,
        productName: item.name,
        price: item.price,
      }));

      // Use existing checkout endpoint with walk-in flag
      if (!transactionData.locationId || transactionData.locationId === -1) {
        toast.error("Please select a specific location");
        setIsProcessing(false);
        return;
      }

      try {
        const statusRes = await baseAxios.get("/register/status", {
          params: { locationId: transactionData.locationId },
        });
        if (!statusRes.data?.isOpen) {
          toast.error(
            "Before recording a walk-in transaction, please open the register first."
          );
          setIsProcessing(false);
          return;
        }
      } catch (err) {
        toast.error(
          "Before recording a walk-in transaction, please open the register first."
        );
        setIsProcessing(false);
        return;
      }

      const response = await baseAxios.post("/scheduler/checkout", {
        appointmentId: null, // No appointment for walk-in
        clientId: transactionData.clientId,
        stylistId: transactionData?.stylistId ?? null,
        services: formattedServices,
        totalAmount: totalWithTip,
        tip: parseFloat(tipAmount.replace(/[^0-9.]/g, "")) || 0,
        paymentMethod: paymentMethod,
        receiptEmail: sendEmail ? trimmedEmailAddress : "",
        locationId: transactionData.locationId,
        isWalkIn: true,
        products: formattedProducts,
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("Transaction completed successfully!");

        if (download) {
          const pdfBlob = await generatePdfBlob();
          if (pdfBlob) {
            const url = URL.createObjectURL(pdfBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `receipt-walkin-${transactionData?.clientName || "client"}-${new Date().toISOString().split("T")[0]}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
          }
        }

        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      }
    } catch (error) {
      console.error("Error creating transaction:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.data?.message ||
          "Failed to complete transaction. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    onBack({
      ...transactionData,
      selectedLocation: transactionData.selectedLocation,
    });
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  const handleConfirmNoReceipt = async () => {
    await completeTransaction({ sendEmail: false, download: false });
  };

  const handleConfirmEmailOnly = async () => {
    await completeTransaction({ sendEmail: true, download: false });
  };

  const handleConfirmDownload = async () => {
    await completeTransaction({ sendEmail: false, download: true });
  };

  if (!transactionData) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6" fontWeight="bold" color="inherit">
            Payment{transactionData?.locationName ? ` · ${transactionData.locationName}` : ""}
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={dialogCloseIconSx}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {transactionData?.locationName && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: `1px solid ${colors.gold}`,
                bgcolor: "rgba(197,160,111,0.12)",
                borderRadius: "6px",
                px: 1.5,
                py: 1,
                mb: 2,
              }}
            >
              <LocationOnIcon sx={{ color: colors.gold, fontSize: 20 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Location
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {transactionData.locationName}
                </Typography>
              </Box>
            </Box>
          )}

          <Box>
            <Typography variant="body2" gutterBottom>
              Total Amount
            </Typography>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#f2f2f2" : colors.black,
              }}
            >
              <span>${isNaN(Number(transactionData?.totalAmount || 0) + Number(tipAmount || 0)) ? transactionData?.totalAmount.toFixed(2) : (Number(transactionData?.totalAmount || 0) + Number(tipAmount || 0)).toFixed(2)}</span>
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" gutterBottom>
              Add Tip (Optional)
            </Typography>
            <TextField
              value={tipAmount}
              onChange={handleTipChange}
              placeholder="0.00"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                inputMode: "decimal",
              }}
            />
          </Box>

          <Box>
            <Typography variant="body2" gutterBottom>
              Payment Method <span style={{ color: "#d32f2f" }}>*</span>
            </Typography>
            <Select
              fullWidth
              size="small"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select payment method
              </MenuItem>
              {PAYMENT_METHODS.map((method) => (
                <MenuItem key={method.value} value={method.value}>
                  {method.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <Typography variant="body2" gutterBottom>
              Email for Receipt (Optional)
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="client@email.com"
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", gap: 1, flexWrap: "wrap" }}
        >
          <Button
            size="small"
            onClick={handleBack}
            disabled={isProcessing}
            sx={secondaryBtnSx}
          >
            Back
          </Button>
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}
          >
            <Button
              size="small"
              onClick={handleConfirmNoReceipt}
              disabled={isProcessing || paymentMethod === ""}
              sx={outlinedBrandBtnSx}
            >
              No Receipt
            </Button>
            <Button
              size="small"
              onClick={handleConfirmEmailOnly}
              variant="contained"
              disabled={isProcessing || paymentMethod === "" || !isValidEmailAddress}
              sx={primaryBtnSx}
            >
              Email Receipt
            </Button>
            <Button
              size="small"
              onClick={handleConfirmDownload}
              variant="outlined"
              disabled={isProcessing || paymentMethod === ""}
              sx={outlinedBrandBtnSx}
            >
              Download Receipt
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionSummaryModal;
